import React from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserCircle2, Filter, Layers } from "lucide-react";
import { StatCard } from "./components/cards";
import { useWarehouseMetrics } from "./warehouse-metrics-api";

// "Uploaded Data Overview" — real GET /api/v1/dashboard/warehouse-metrics
// (overview group). Labels relabeled to read as one lead funnel (Total
// Leads -> Active leads (Last 7 day) -> Cold Prospects, with Customers as
// the converted endpoint) per the reviewed design.
export const UploadedDataOverviewSection: React.FC = () => {
  const navigate = useNavigate();
  const { loading, findMetric, formatValue } = useWarehouseMetrics();
  const totalLeads = findMetric("overview", ["total_leads", "total_contacts"], ["Total Leads", "Total Contacts"]);
  const customers = findMetric("overview", ["customers"], ["Customers"]);
  const activeLeads = findMetric("overview", ["active_leads"], ["Active Leads", "Active leads (Last 7 day)"]);
  const coldProspects = findMetric("overview", ["cold_prospects", "prospects"], ["Cold Prospects", "Prospects"]);

  return (
    <section>
      <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Uploaded Data Overview</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
        See a snapshot of the contacts and customer records currently available from your uploaded data.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Users size={16} />}
          iconClassName="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
          label="Total Leads"
          value={formatValue(totalLeads)}
          description="All contact records currently available across your uploaded data."
          linkLabel="View Contacts"
          onLinkClick={() => navigate("/unify/profiles")}
          loading={loading}
          isSample={totalLeads?.is_sample}
        />
        <StatCard
          icon={<UserCircle2 size={16} />}
          iconClassName="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
          label="Customers"
          value={formatValue(customers)}
          description="Contacts identified as existing or active customers."
          linkLabel="View Customers"
          onLinkClick={() => navigate("/unify/profiles")}
          loading={loading}
          isSample={customers?.is_sample}
        />
        <StatCard
          icon={<Filter size={16} />}
          iconClassName="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
          label="Active leads (Last 7 day)"
          value={formatValue(activeLeads)}
          description="Leads currently being engaged or followed up on by your team."
          linkLabel="View Active Leads"
          onLinkClick={() => navigate("/leads")}
          loading={loading}
          isSample={activeLeads?.is_sample}
        />
        <StatCard
          icon={<Layers size={16} />}
          iconClassName="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
          label="Cold Prospects"
          value={formatValue(coldProspects)}
          description="Potential customers who have not yet converted or become active customers."
          linkLabel="View Prospects"
          onLinkClick={() => navigate("/leads")}
          loading={loading}
          isSample={coldProspects?.is_sample}
        />
      </div>
    </section>
  );
};

export default UploadedDataOverviewSection;
